/**
 * Pengaturan Notifikasi pembeli (storefront).
 *
 * Notifikasi dibuat server-side via SQL (`insert_notification`), jadi preferensi
 * ini bersifat PREFERENSI TAMPILAN: jenis yang dinonaktifkan pembeli akan
 * disembunyikan dari daftar `/notifications` dan tidak ikut dihitung di badge
 * bell — barisnya tetap ada di DB, hanya tidak ditampilkan. Disimpan di
 * localStorage (model opt-out: default semua aktif), pola sama dengan
 * `recentlyViewed.ts`.
 *
 * Inti logika murni (katalog jenis, toggle, filter) dipisah dari wrapper browser
 * supaya bisa diuji di node tanpa DOM.
 */

export const NOTIF_PREFS_KEY = "galea-notif-prefs";

/** Event window yang dipancarkan saat preferensi berubah (untuk update live bell
 * di tab yang sama; `storage` event hanya lintas-tab). */
export const NOTIF_PREFS_EVENT = "galea-notif-prefs-changed";

export type NotifCategory =
  | "pengumuman"
  | "pesanan"
  | "pengiriman"
  | "tagihan"
  | "saldo"
  | "poin"
  | "akun";

export const NOTIF_CATEGORY_LABEL: Record<NotifCategory, string> = {
  pengumuman: "Pengumuman",
  pesanan: "Pesanan",
  pengiriman: "Pengiriman",
  tagihan: "Tagihan",
  saldo: "Saldo",
  poin: "Poin",
  akun: "Akun",
};

/** Urutan tampil kategori di halaman pengaturan. */
export const NOTIF_CATEGORY_ORDER: NotifCategory[] = [
  "pengumuman",
  "pesanan",
  "pengiriman",
  "tagihan",
  "saldo",
  "poin",
  "akun",
];

export type NotifTypeMeta = {
  /** Nilai `notifications.type` (CHECK constraint, 19 nilai). */
  type: string;
  category: NotifCategory;
  /** Judul ramah untuk daftar pengaturan. */
  label: string;
  /** Keterangan singkat kapan notifikasi ini dikirim. */
  description: string;
  /** Emoji (selaras `getNotifIcon` di NotificationsClient). */
  icon: string;
  /** Contoh notifikasi seperti yang muncul di daftar. */
  example: { title: string; body: string };
};

/**
 * Katalog 19 jenis notifikasi pembeli. Contoh diambil dari teks asli RPC SQL
 * (`insert_notification`) supaya pratinjaunya realistis.
 */
export const NOTIF_TYPES: NotifTypeMeta[] = [
  // ── Pengumuman ─────────────────────────────────────────────────────────────
  {
    type: "admin_broadcast",
    category: "pengumuman",
    label: "Pengumuman",
    description: "Broadcast/pengumuman dari admin (promo, info penting).",
    icon: "📢",
    example: {
      title: "Promo Gajian 25%",
      body: "Khusus hari ini, semua produk diskon 25%. Cek sekarang sebelum kehabisan!",
    },
  },
  // ── Pesanan ──────────────────────────────────────────────────────────────
  {
    type: "po_closed",
    category: "pesanan",
    label: "PO Ditutup",
    description: "Saat PO produk yang kamu pesan ditutup admin.",
    icon: "🔒",
    example: {
      title: "PO Ditutup",
      body: "PO Tas Luna sudah ditutup. Pesananmu sudah dikunci, tunggu proses produksi.",
    },
  },
  {
    type: "production_started",
    category: "pesanan",
    label: "Produksi Dimulai",
    description: "Saat pesanan PO-mu masuk tahap produksi.",
    icon: "🏭",
    example: {
      title: "Produksi Dimulai",
      body: "Pesananmu untuk Tas Luna masuk tahap produksi.",
    },
  },
  {
    type: "item_ready",
    category: "pesanan",
    label: "Barang Siap",
    description: "Saat barang pesananmu sudah ready & masuk keranjang.",
    icon: "📦",
    example: {
      title: "Barang Siap!",
      body: "Tas Luna (Merah / L) sudah ready di keranjangmu. Segera pilih untuk dikirim!",
    },
  },
  {
    type: "keep_expiring_soon",
    category: "pesanan",
    label: "Keep Akan Hangus",
    description: "Sehari sebelum keep ready stock-mu hangus (peringatan H-1).",
    icon: "⏳",
    example: {
      title: "Keep Akan Hangus",
      body: "Keep Tas Luna akan hangus pada 22 Jun 08:00 WIB. Segera bayar agar stok tidak dilepas.",
    },
  },
  {
    type: "keep_expired",
    category: "pesanan",
    label: "Keep Hangus",
    description: "Saat keep ready stock hangus karena belum dibayar.",
    icon: "⏰",
    example: {
      title: "Keep Hangus",
      body: "Keep Tas Luna hangus karena belum dibayar lebih dari 7 hari. Stok sudah dilepas.",
    },
  },
  {
    type: "order_cancelled",
    category: "pesanan",
    label: "Pesanan Dibatalkan",
    description: "Saat item pesananmu dibatalkan admin.",
    icon: "❌",
    example: {
      title: "Pesanan Dibatalkan",
      body: "Item pesanan Tas Luna telah dibatalkan oleh admin.",
    },
  },
  {
    type: "order_updated",
    category: "pesanan",
    label: "Pesanan Diperbarui",
    description: "Saat pesanan atau pengirimanmu diubah admin.",
    icon: "✏️",
    example: {
      title: "Pengiriman Dibatalkan",
      body: "Pengiriman pesananmu dibatalkan. Item dikembalikan ke keranjang.",
    },
  },
  {
    type: "production_failed",
    category: "pesanan",
    label: "Gagal Produksi",
    description: "Saat item gagal diproduksi & dana dikembalikan ke saldo.",
    icon: "⚠️",
    example: {
      title: "Item Gagal Produksi",
      body: "Mohon maaf, item Tas Luna (Merah / L) gagal diproduksi dan dibatalkan. Dana dikembalikan ke saldo.",
    },
  },
  // ── Pengiriman ───────────────────────────────────────────────────────────
  {
    type: "shipment_ongkir_set",
    category: "pengiriman",
    label: "Ongkir Ditetapkan",
    description: "Saat ongkir pengirimanmu ditetapkan & ditagihkan.",
    icon: "🚚",
    example: {
      title: "Ongkir Ditetapkan",
      body: "Ongkir kirimanmu Rp 20.000 ditagihkan sebagai Tagihan #123. Silakan lakukan pembayaran.",
    },
  },
  {
    type: "shipment_payment_confirmed",
    category: "pengiriman",
    label: "Pengiriman Disiapkan",
    description: "Saat pembayaran/penyiapan pengirimanmu dikonfirmasi.",
    icon: "✅",
    example: {
      title: "Pembayaran Dikonfirmasi",
      body: "Pembayaran kirimanmu sudah dikonfirmasi. Pesanan sedang disiapkan untuk dikirim!",
    },
  },
  {
    type: "item_shipped",
    category: "pengiriman",
    label: "Pesanan Dikirim",
    description: "Saat pesananmu dikirim atau siap diambil di toko.",
    icon: "📬",
    example: {
      title: "Pesanan Dikirim!",
      body: "Pesananmu dikirim via JNE. No. resi: JNE1234567890",
    },
  },
  // ── Tagihan ──────────────────────────────────────────────────────────────
  {
    type: "invoice_issued",
    category: "tagihan",
    label: "Tagihan Baru",
    description: "Saat tagihan baru diterbitkan untukmu.",
    icon: "🧾",
    example: {
      title: "Tagihan Baru",
      body: "Tagihan #123 sebesar Rp 150.000 diterbitkan. Cek menu Tagihan untuk membayar (boleh dicicil).",
    },
  },
  {
    type: "invoice_paid",
    category: "tagihan",
    label: "Tagihan Lunas",
    description: "Saat tagihanmu sudah lunas.",
    icon: "✅",
    example: {
      title: "Tagihan Lunas",
      body: "Tagihan #123 sudah lunas. Terima kasih!",
    },
  },
  {
    type: "invoice_rejected",
    category: "tagihan",
    label: "Bukti Transfer Ditolak",
    description: "Saat bukti transfer atau pengajuan tagihanmu ditolak.",
    icon: "🚫",
    example: {
      title: "Bukti Transfer Ditolak",
      body: "Bukti transfer untuk Tagihan #123 ditolak admin. Silakan unggah ulang dari menu Tagihan.",
    },
  },
  {
    type: "invoice_auto_voided",
    category: "tagihan",
    label: "Tagihan Kedaluwarsa",
    description:
      "Saat tagihanmu dibatalkan otomatis karena lewat batas waktu pembayaran.",
    icon: "🗓️",
    example: {
      title: "Tagihan Dibatalkan Otomatis",
      body: "Tagihan #123 dibatalkan otomatis karena melewati batas waktu pembayaran. Barangnya kembali ke keranjang dan masih bisa ditagihkan ulang selama stoknya belum habis.",
    },
  },
  // ── Saldo ────────────────────────────────────────────────────────────────
  {
    type: "topup_approved",
    category: "saldo",
    label: "Top-up Disetujui",
    description: "Saat permintaan top-up saldomu disetujui.",
    icon: "💰",
    example: {
      title: "Top-up Disetujui",
      body: "Top-up Rp 100.000 disetujui. Saldo kamu sekarang Rp 250.000.",
    },
  },
  {
    type: "topup_rejected",
    category: "saldo",
    label: "Top-up Ditolak",
    description: "Saat permintaan top-up saldomu ditolak.",
    icon: "💸",
    example: {
      title: "Top-up Ditolak",
      body: "Top-up Rp 100.000 ditolak.",
    },
  },
  {
    type: "balance_credited",
    category: "saldo",
    label: "Saldo Ditambahkan",
    description: "Saat admin menambah saldomu.",
    icon: "💰",
    example: {
      title: "Saldo Ditambahkan",
      body: "Admin menambahkan Rp 50.000 ke saldo kamu. Catatan: Bonus loyalitas.",
    },
  },
  {
    type: "balance_debited",
    category: "saldo",
    label: "Saldo Dikurangi",
    description: "Saat admin mengurangi saldomu.",
    icon: "💸",
    example: {
      title: "Saldo Dikurangi",
      body: "Admin mengurangi Rp 20.000 dari saldo kamu. Catatan: Koreksi transaksi.",
    },
  },
  // ── Poin ─────────────────────────────────────────────────────────────────
  {
    type: "points_earned",
    category: "poin",
    label: "Poin Bertambah",
    description: "Saat kamu mendapat poin dari pembelian yang lunas.",
    icon: "⭐",
    example: {
      title: "Poin Bertambah",
      body: 'Kamu dapat 6 poin dari program "Promo Merek A". 🎉',
    },
  },
  {
    type: "redemption_fulfilled",
    category: "poin",
    label: "Hadiah Diproses",
    description: "Saat klaim hadiah/voucher poinmu diproses admin.",
    icon: "🎁",
    example: {
      title: "Hadiah Diproses",
      body: 'Klaim hadiah "Voucher Belanja Rp50.000" telah diproses admin.',
    },
  },
  {
    type: "redemption_rejected",
    category: "poin",
    label: "Klaim Hadiah Ditolak",
    description: "Saat klaim hadiah ditolak; poin dikembalikan.",
    icon: "↩️",
    example: {
      title: "Klaim Hadiah Ditolak",
      body: "Klaim hadiahmu ditolak; poin telah dikembalikan.",
    },
  },
  {
    type: "points_revoked",
    category: "poin",
    label: "Poin Ditarik",
    description: "Saat poin ditarik karena tagihan di-refund/dibatalkan.",
    icon: "⚠️",
    example: {
      title: "Poin Ditarik",
      body: "6 poin ditarik karena tagihan di-refund/dibatalkan.",
    },
  },
  {
    type: "points_expiring_soon",
    category: "poin",
    label: "Poin Akan Hangus",
    description: "Peringatan H-1 sebelum poin kedaluwarsa.",
    icon: "⏳",
    example: {
      title: "Poin Akan Hangus",
      body: "40 poin akan hangus pada 30 Jun 23:59 WIB. Segera tukarkan!",
    },
  },
  {
    type: "points_expired",
    category: "poin",
    label: "Poin Kedaluwarsa",
    description: "Saat poin kamu kedaluwarsa.",
    icon: "🕒",
    example: {
      title: "Poin Kedaluwarsa",
      body: "40 poin kamu telah kedaluwarsa.",
    },
  },
  // ── Akun ─────────────────────────────────────────────────────────────────
  {
    type: "level_up",
    category: "akun",
    label: "Naik Level",
    description: "Saat level reseller-mu naik.",
    icon: "🎉",
    example: {
      title: "Selamat, level kamu naik! 🎉",
      body: "Kamu sekarang Reseller Gold. Nikmati harga & benefit khusus level barumu.",
    },
  },
  {
    type: "membership_expiring_soon",
    category: "akun",
    label: "Keanggotaan Akan Berakhir",
    description:
      "Peringatan 7 hari sebelum level mitra turun otomatis karena tidak ada transaksi.",
    icon: "⏳",
    example: {
      title: "Keanggotaan Akan Berakhir",
      body: "Level Reseller kamu berakhir sekitar 26 Aug 2026 bila tidak ada transaksi baru. Belanja apa pun memperpanjang keanggotaan otomatis.",
    },
  },
  {
    type: "membership_expired",
    category: "akun",
    label: "Keanggotaan Berakhir",
    description: "Saat level mitra turun otomatis karena masa aktif habis.",
    icon: "📪",
    example: {
      title: "Keanggotaan Berakhir",
      body: "Level Reseller kamu berakhir karena tidak ada transaksi selama 6 bulan. Kamu bisa daftar lagi kapan saja di halaman Mitra.",
    },
  },
  {
    type: "feedback_replied",
    category: "akun",
    label: "Balasan Kritik & Saran",
    description: "Saat admin membalas kritik & saran kamu.",
    icon: "💬",
    example: {
      title: "Masukanmu sudah dibalas",
      body: "Mohon maaf atas keterlambatannya, sudah kami tindak lanjuti ke tim gudang.",
    },
  },
];

/** Map type → metadata (sekali bangun, dipakai lookup ikon/label). */
const METADATA_BY_TYPE: Map<string, NotifTypeMeta> = new Map(
  NOTIF_TYPES.map((meta) => [meta.type, meta])
);

export function notifMeta(type: string): NotifTypeMeta | undefined {
  return METADATA_BY_TYPE.get(type);
}

/** Ikon notifikasi by type, fallback 🔔 untuk jenis tak dikenal. */
export function notifIcon(type: string): string {
  return METADATA_BY_TYPE.get(type)?.icon ?? "🔔";
}

export function notifTypesByCategory(category: NotifCategory): NotifTypeMeta[] {
  return NOTIF_TYPES.filter((meta) => meta.category === category);
}

/**
 * Preferensi pembeli: type → aktif?. Tersimpan sebagai patch (model opt-out),
 * jadi hanya jenis yang DINONAKTIFKAN yang perlu disimpan `false`.
 */
export type NotifPrefs = Record<string, boolean>;

/**
 * Aktif jika tidak di-set `false`. Default aktif → jenis baru dari server
 * (yang belum ada di katalog) tetap tampil.
 */
export function isNotifEnabled(prefs: NotifPrefs, type: string): boolean {
  return prefs[type] !== false;
}

/** Toggle satu jenis; mengembalikan objek prefs baru (immutable). */
export function setNotifEnabled(
  prefs: NotifPrefs,
  type: string,
  enabled: boolean
): NotifPrefs {
  return { ...prefs, [type]: enabled };
}

/** Set SEMUA jenis di katalog ke nilai yang sama (Aktifkan/Nonaktifkan Semua). */
export function setAllNotifPrefs(
  prefs: NotifPrefs,
  enabled: boolean
): NotifPrefs {
  const next: NotifPrefs = { ...prefs };
  for (const meta of NOTIF_TYPES) next[meta.type] = enabled;
  return next;
}

/** Jumlah jenis katalog yang aktif. */
export function countEnabled(prefs: NotifPrefs): number {
  return NOTIF_TYPES.reduce(
    (acc, meta) => acc + (isNotifEnabled(prefs, meta.type) ? 1 : 0),
    0
  );
}

/** True bila SEMUA jenis katalog aktif. */
export function allNotifEnabled(prefs: NotifPrefs): boolean {
  return NOTIF_TYPES.every((meta) => isNotifEnabled(prefs, meta.type));
}

/** Saring daftar notifikasi → hanya jenis yang aktif. */
export function filterEnabledNotifs<T extends { type: string; important?: boolean | null }>(
  items: T[],
  prefs: NotifPrefs
): T[] {
  // Notif "penting" (broadcast kritis) tetap tampil walau jenisnya di-opt-out.
  return items.filter(
    (item) => item.important === true || isNotifEnabled(prefs, item.type)
  );
}

/** Parse nilai mentah localStorage → prefs valid; tak pernah throw. */
export function sanitizeNotifPrefs(raw: unknown): NotifPrefs {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: NotifPrefs = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "boolean") out[key] = value;
  }
  return out;
}

// ── Wrapper browser (localStorage; best-effort, tak pernah throw) ────────────

export function readNotifPrefs(): NotifPrefs {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(NOTIF_PREFS_KEY);
    if (!raw) return {};
    return sanitizeNotifPrefs(JSON.parse(raw) as unknown);
  } catch {
    return {};
  }
}

export function writeNotifPrefs(prefs: NotifPrefs): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new Event(NOTIF_PREFS_EVENT));
  } catch {
    // abaikan — fitur opsional
  }
}
